import { memo, useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { spacing, shadows } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';

const FigmaModal = memo(({ isOpen, onClose }) => {
  const { colors } = useTheme();
  const [buttonHover, setButtonHover] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    const handleClickOutside = (e) => {
      if (e.target.classList.contains('modal-backdrop')) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  return (
    <div 
      className="modal-backdrop"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: spacing.xl,
        animation: 'fadeIn 0.2s ease-out'
      }}
    >
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
      
      <div style={{
        background: colors.backgroundPanel,
        borderRadius: '8px',
        maxWidth: '420px',
        width: '100%',
        padding: spacing.xl,
        boxShadow: shadows.md,
        animation: 'slideUp 0.3s ease-out',
        position: 'relative'
      }}>
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: spacing.lg,
            right: spacing.lg,
            width: '32px',
            height: '32px',
            borderRadius: '4px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors.foregroundSecondary,
            transition: 'all 0.15s ease',
            fontSize: '1.25rem',
            fontWeight: 300,
            outline: 'none',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = colors.backgroundOffset;
            e.currentTarget.style.color = colors.foreground;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = colors.foregroundSecondary;
          }}
          aria-label="Close"
        >
          ×
        </button>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.md,
          marginBottom: spacing.lg
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            background: colors.backgroundOffset,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Check size={24} color={colors.foreground} />
          </div>
          <h2 style={{
            margin: 0,
            fontSize: '1.25rem',
            fontWeight: 600,
            color: colors.foreground
          }}>
            Text Copied!
          </h2>
        </div>
        
        <p style={{
          margin: `0 0 ${spacing.lg} 0`,
          fontSize: '0.875rem',
          lineHeight: '1.6',
          color: colors.foregroundSecondary,
          textAlign: 'left'
        }}>
          To paste the ASCII art into Figma:
        </p>
        
        <ol style={{
          margin: 0,
          paddingLeft: spacing.xl,
          fontSize: '0.875rem',
          lineHeight: '1.8',
          color: colors.foreground,
          textAlign: 'left'
        }}>
          <li style={{ marginBottom: spacing.sm }}>
            Press <kbd style={{
              padding: `2px ${spacing.sm}`,
              background: colors.backgroundOffset,
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              border: `1px solid ${colors.border}`
            }}>T</kbd> to create a text box in Figma
          </li>
          <li style={{ marginBottom: spacing.sm }}>
            Paste with <kbd style={{
              padding: `2px ${spacing.sm}`,
              background: colors.backgroundOffset,
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              border: `1px solid ${colors.border}`
            }}>Cmd</kbd> + <kbd style={{
              padding: `2px ${spacing.sm}`,
              background: colors.backgroundOffset,
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              border: `1px solid ${colors.border}`
            }}>V</kbd> (or <kbd style={{
              padding: `2px ${spacing.sm}`,
              background: colors.backgroundOffset,
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              border: `1px solid ${colors.border}`
            }}>Ctrl</kbd> + <kbd style={{
              padding: `2px ${spacing.sm}`,
              background: colors.backgroundOffset,
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              border: `1px solid ${colors.border}`
            }}>V</kbd>)
          </li>
          <li style={{ marginBottom: spacing.sm }}>
            Set font to <strong>Courier</strong>, <strong>Monaco</strong>, or any monospace font
          </li>
          <li>
            Make sure the font supports the characters (like <code style={{
              fontFamily: 'monospace',
              background: colors.backgroundOffset,
              padding: `2px 4px`,
              borderRadius: '4px'
            }}>█▓▒░</code>)
          </li>
        </ol>
        
        <div style={{
          marginTop: spacing.xl,
          display: 'flex',
          justifyContent: 'flex-start'
        }}>
          <button
            onClick={onClose}
            onMouseEnter={() => setButtonHover(true)}
            onMouseLeave={() => setButtonHover(false)}
            style={{
              height: '36px',
              padding: '0 14px',
              background: colors.foreground,
              color: colors.background,
              border: 'none',
              borderRadius: '4px',
              fontSize: '0.8125rem',
              fontWeight: 500,
              letterSpacing: '-0.005em',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              outline: 'none',
              opacity: buttonHover ? 0.85 : 1,
            }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
});

export default FigmaModal;